<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, type ComputedRef } from "vue";
import type { RouteLocationRaw } from "vue-router";
import type { TenantSummary } from "../api/tenants.js";
import { useTenantStore } from "../stores/tenants.js";

interface ProbeTab {
  readonly label: string;
  readonly routeName: string;
  readonly routeParameters: Readonly<Record<string, string>>;
}

const tenantStore: ReturnType<typeof useTenantStore> = useTenantStore();
const tenant: ComputedRef<TenantSummary | null> = computed(
  (): TenantSummary | null => tenantStore.selectedTenant,
);
const tabs: ComputedRef<readonly ProbeTab[]> = computed(
  (): readonly ProbeTab[] => {
    const currentTenant: TenantSummary | null = tenant.value;
    if (currentTenant === null) {
      return [];
    }

    const items: ProbeTab[] = [
      {
        label: "Inventory",
        routeName: "tenant-probes",
        routeParameters: { tenantId: currentTenant.id },
      },
      {
        label: "Rollout",
        routeName: "tenant-probe-rollout",
        routeParameters: { tenantId: currentTenant.id },
      },
    ];
    if (currentTenant.access.capabilities.includes("probe.configuration.manage")) {
      items.push({
        label: "Configuration",
        routeName: "tenant-probe-configuration",
        routeParameters: { tenantId: currentTenant.id },
      });
    }
    return items;
  },
);

function tabTarget(tab: ProbeTab): RouteLocationRaw {
  return { name: tab.routeName, params: tab.routeParameters };
}
</script>

<template>
  <nav v-if="tabs.length > 1" class="page-tabs" aria-label="Probe sections">
    <RouterLink
      v-for="tab in tabs"
      :key="tab.routeName"
      class="page-tab"
      :to="tabTarget(tab)"
    >
      {{ tab.label }}
    </RouterLink>
  </nav>
</template>
